import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import GemSellRules from "@/components/GemSellRules";
import {
  errorText, formatPHP, METHOD_LABEL, newRequestId, normaliseKey, normaliseMobile, payoutFor, saleWords,
} from "@/lib/gems";

const FIELD = "mt-1 w-full min-w-0 border border-border rounded-sm px-3 py-2 bg-background disabled:opacity-60";
const DEFAULT_METHODS = [{ code: "gcash", label: "GCash" }, { code: "maya", label: "Maya" }];
const UNCLEAR = "We could not confirm whether the sale was made. Check your sales history before selling again. "
  + "Pressing Sell gems again is safe: it cannot make a second sale.";

// A 4xx means the server looked at this sale and said no, so its request_id
// is spent. A network error or a 5xx is a maybe, and a retry must carry the
// SAME id so the server replays the sale it holds instead of making a second.
function isDefinitiveRefusal(err) {
  const status = err?.response?.status;
  return typeof status === "number" && status >= 400 && status < 500;
}

const isWhole = (v) => Number.isInteger(v) && v >= 0;

// The fee this page quotes (operator, 2026-10-16: PHP 35 on a player's first
// sale, PHP 10 after). Once the account is linked the server says which one
// THIS player pays (`next_fee_php`); before that the first-sale fee is quoted,
// the higher of the two, so a player is never shown more than they receive.
function quotedFee(config, nextFee) {
  if (isWhole(nextFee)) return nextFee;
  if (!config) return null;
  if (isWhole(config.first_sale_fee_php)) return config.first_sale_fee_php;
  return isWhole(config.fee_php) ? config.fee_php : null;
}

function resultWords(sale, method) {
  const state = sale?.state;
  if (state === "sent" || state === "paid") return `Sent to your ${METHOD_LABEL[sale.method || method] || "GCash or Maya"}`;
  if (state === "review" || (state === "requested" && sale.eplq_sale_state === "settled")) return "Being reviewed";
  return saleWords(sale);
}

// The Gem Collector's storefront: it buys gems, it sells nothing. The form
// links the seller's ePlayQuest account with a single-use Treasure Chest Key,
// then sells, in two calls. The fee and limits are read from the server so
// this page never promises a figure the exchange does not pay; if they cannot
// be read, no figure is shown and nothing can be sold from here.
export default function GemSellCallout({ store }) {
  const auth = useAuth() || {};
  const [config, setConfig] = useState(null);
  const [gems, setGems] = useState("");
  const [key, setKey] = useState("");
  const [method, setMethod] = useState("gcash");
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  // One id per sale: minted on the first submit, dropped once the server
  // answers with a sale, and otherwise dropped on a definitive refusal or
  // when the sale itself (amount, method, number) is edited. Editing the key
  // never drops it.
  //
  // After an UNCLEAR failure (network error, 5xx) the first sale may have
  // landed, so the id is kept through every edit and every refusal until a
  // later attempt is answered with a sale: the server replays the sale it
  // holds for a known id, so the page can never make a second one by
  // accident. `unclear` is that state, and it shows a line saying so.
  const requestId = useRef(null);
  const unclearRef = useRef(false);
  const [unclear, setUnclear] = useState(false);
  const inFlight = useRef(false);
  // The key this page has linked with and not yet sold under. Keys are single
  // use, so a retry of a failed sale does not send it to ePlayQuest again;
  // after a sale it is forgotten, so every sale proves control afresh.
  const linkedKey = useRef(null);
  // THIS player's fee, once the server has said (null until then).
  const [nextFee, setNextFee] = useState(null);

  useEffect(() => {
    let live = true;
    api.get("/exchange/config").then((r) => { if (live) setConfig(r.data || null); }).catch(() => {});
    return () => { live = false; };
  }, []);

  const refreshFee = async () => {
    try {
      const { data } = await api.get("/exchange/link");
      setNextFee(data?.linked && isWhole(data.next_fee_php) ? data.next_fee_php : null);
    } catch (err) {
      // keep the fee already quoted; the server refuses a sale at another fee
    }
  };
  const userId = auth.user?.id;
  useEffect(() => {
    if (userId) refreshFee();
    else setNextFee(null);
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const signedOut = !auth.loading && !auth.user;
  const closed = !!config && !config.enabled;
  const fee = quotedFee(config, nextFee);
  const priced = fee !== null;
  const quote = useMemo(() => (priced ? payoutFor(gems, config, fee) : null), [priced, gems, config, fee]);
  const normalKey = normaliseKey(key);
  const number = normaliseMobile(mobile);
  const ready = !!auth.user && !!config && !!config.enabled && !!quote && quote.ok && !!normalKey && !!number;
  const methods = config?.methods?.length ? config.methods : DEFAULT_METHODS;
  const name = store?.name || "The Gem Collector";
  const signInTo = store?.slug ? `/login?redirect=${encodeURIComponent(`/store/${store.slug}`)}` : "/login";

  const edited = (setter, saleField) => (e) => {
    setter(e.target.value);
    setError("");
    setResult(null);
    if (saleField && !unclearRef.current) requestId.current = null;
  };

  const markUnclear = (value) => {
    unclearRef.current = value;
    setUnclear(value);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    setResult(null);
    if (!requestId.current) requestId.current = newRequestId();
    try {
      if (linkedKey.current !== normalKey) {
        let linked;
        try {
          // Never retried: each verify spends one of the account's attempts.
          ({ data: linked } = await api.post("/exchange/link", { key: normalKey }));
        } catch (err) {
          setError(errorText(err, "Your ePlayQuest account could not be linked. Try again"));
          return;
        }
        linkedKey.current = normalKey;
        // The key has named the player, so the server now knows their fee. If
        // it is not the one this page quoted, re-quote and let them press
        // again rather than sell at a figure they were not shown.
        const theirs = linked?.next_fee_php;
        if (isWhole(theirs) && theirs !== fee) {
          setNextFee(theirs);
          setError(`Your fee is ${formatPHP(theirs)}, so you receive ${formatPHP(Math.max(quote.gems - theirs, 0))}. `
            + "Press Sell gems to sell at that fee.");
          return;
        }
      }
      try {
        const { data } = await api.post("/exchange/sales", {
          gems: quote.gems, method, mobile_number: number, request_id: requestId.current,
          expected_fee_php: fee,
        });
        // Answered: whatever happened before, this is the sale the id names.
        requestId.current = null;
        linkedKey.current = null;
        markUnclear(false);
        setResult(data || {});
        setGems("");
        setKey("");
      } catch (err) {
        if (isDefinitiveRefusal(err)) {
          // A refusal says nothing certain about an earlier unclear attempt
          // (some are decided before the server looks the id up), so the id
          // is only dropped when nothing is unclear.
          if (!unclearRef.current) requestId.current = null;
          setError(errorText(err, "The sale did not go through"));
          if (err?.response?.data?.detail?.code === "FEE_CHANGED") await refreshFee();
        } else {
          markUnclear(true);
        }
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  let payoutLine = null;
  if (quote && String(gems).trim() !== "") {
    payoutLine = quote.ok
      ? `You receive ${formatPHP(quote.payout)} after the ${formatPHP(fee)} ${isWhole(nextFee) ? "" : "first-sale "}fee`
      : quote.reason;
  }

  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-14" data-testid="gem-sell-callout">
      <h2 className="font-display text-2xl md:text-4xl font-black tracking-tight text-primary text-center">
        Sell your gems to {name}
      </h2>
      {config && <GemSellRules config={config} className="max-w-md mx-auto mt-6" />}
      <form onSubmit={submit} className="v-card mt-6 p-6 space-y-4 max-w-md mx-auto" data-testid="gem-sell-form">
        <label className="block text-sm">
          Amount
          <input name="gems" value={gems} inputMode="numeric" autoComplete="off" disabled={closed}
            placeholder={config ? `${Number(config.min_gems).toLocaleString("en-PH")} to ${Number(config.max_gems).toLocaleString("en-PH")} gems` : "Gems"}
            className={FIELD} onChange={edited(setGems, true)} />
        </label>
        <label className="block text-sm">
          Treasure Chest Key
          <input name="key" value={key} autoComplete="off" autoCapitalize="characters" spellCheck={false}
            maxLength={20} placeholder="XXXX-XXXX-XXXX" disabled={closed} className={FIELD}
            onChange={edited(setKey, false)} />
        </label>
        <label className="block text-sm">
          GCash or Maya
          <select name="method" value={method} disabled={closed} className={FIELD} onChange={edited(setMethod, true)}>
            {methods.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
          </select>
        </label>
        <label className="block text-sm">
          Mobile number
          <input name="mobile" value={mobile} inputMode="tel" autoComplete="tel" placeholder="09XXXXXXXXX"
            disabled={closed} className={FIELD} onChange={edited(setMobile, true)} />
        </label>
        {payoutLine && <p className="font-semibold" data-testid="gem-sell-payout">{payoutLine}</p>}
        {unclear && (
          <div role="alert" className="text-sm space-y-1" data-testid="gem-sell-unclear">
            <p>{UNCLEAR}</p>
            <Link to="/app/gems" className="underline">Open your sales history</Link>
          </div>
        )}
        {error && <p role="alert" className="text-sm text-destructive break-words" data-testid="gem-sell-error">{error}</p>}
        {result && (
          <div role="status" className="text-sm space-y-1" data-testid="gem-sell-result">
            <p className="font-semibold">{resultWords(result, method)}</p>
            <Link to="/app/gems" className="underline">See your sales history</Link>
          </div>
        )}
        {signedOut ? (
          <Link to={signInTo} className="v-btn-primary w-full justify-center" data-testid="gem-sell-signin">
            Sign in to sell
          </Link>
        ) : (
          <button type="submit" className="v-btn-primary w-full justify-center" disabled={!ready || busy}
            data-testid="gem-sell-submit">
            Sell gems
          </button>
        )}
      </form>
    </section>
  );
}
